import { isPresent, walkFormConfig, type DocumentTemplate, type SuperFormDefinition, type SuperFormPrefillMap } from '@mes-amis/super-form';
import { toIsoDate } from '@/utils/date_utils';
import { skipsPrefillConfirmation } from '@/db/types/document_template';

/**
 * Provenance label for answers carried forward by the Duplicate feature. MUST
 * stay byte-for-byte identical to the monami server
 * (`SuperForm::Table::Prefill::DUPLICATE_PREFILL_SOURCE`) so an offline-rendered
 * duplicate matches what the server recomputes on sync — update both together.
 */
export const DUPLICATE_PREFILL_SOURCE = 'Duplicated document';

/**
 * Build `field_prefills` for a duplicated document: every editable, answered
 * leaf field becomes a confirm-required prefill. Mirrors the server's
 * `SuperForm::Table::Prefill#duplicate_prefill_slugs` — editable, non-derived
 * leaf fields with a value, INCLUDING core-data fields (the expanded
 * Person::Address/Names/etc. leaves) and NeedScores assessment rows (keyed on
 * each row's `.score` slug). The offline copy is best-effort for pre-sync
 * rendering — the server recomputes authoritatively on sync.
 *
 * The provenance shown to the assessor is the source document's name +
 * completion date (`document_name`/`date`), matching the server.
 * `sourceCompletedAt` is any parseable date string; it's normalized to the
 * `YYYY-MM-DD` contract the client renders.
 *
 * A template with `skip_prefill_confirmation` accepts the copied
 * answers as-is: no prefill entries at all, matching the server which removes
 * the duplicated slugs from `field_prefills`.
 *
 * `coreDataSnapshot` is the client's `core_data_snapshot` (its freshest copy of
 * the live person record). The server derives core-data (`person.*`) slugs from
 * the LIVE person via MethodChain — not from the copied answers — so a core
 * leaf whose live value diverges from the archived copy (a phone added since
 * the source was completed, say) is counted server-side but was missed here,
 * leaving one extra unconfirmed prefill after sync (GHIB-543). When the
 * snapshot carries a key, it wins for core leaves; absent keys fall back to
 * the copied answer (best effort).
 */
export function buildDuplicatePrefills(
  superForm: Record<string, unknown> | undefined,
  definition: SuperFormDefinition | undefined,
  source?: { name?: string; completedAt?: string | null },
  template?: DocumentTemplate | null,
  coreDataSnapshot?: Record<string, unknown>,
): SuperFormPrefillMap {
  const out: SuperFormPrefillMap = {};
  if (!superForm || !definition?.form) return out;
  if (skipsPrefillConfirmation(template)) return out;

  const documentName = source?.name?.trim() || undefined;
  const date = toIsoDate(source?.completedAt);
  const entry = {
    source: DUPLICATE_PREFILL_SOURCE,
    requires_confirmation: true as const,
    ...(documentName ? { document_name: documentName } : {}),
    ...(date ? { date } : {}),
  };

  walkFormConfig(definition.form, ({ name, config }) => {
    // NeedScores grids have no per-row leaf field; emit a prefill for each
    // answered row keyed on its `.score` slug — the same slug the server emits
    // and the client (NeedScoresField) keys its row indicator on.
    if (config?.type === 'NeedScores') {
      const ns = config as unknown as { scope?: string; definitions?: Array<{ slug?: string }> };
      if (ns.scope && Array.isArray(ns.definitions)) {
        for (const def of ns.definitions) {
          if (!def?.slug) continue;
          const slug = `person.assessments_need_scores.${ns.scope}.${def.slug}.score`;
          if (isPresent(superForm[slug])) out[slug] = entry;
        }
      }
      return;
    }

    // Core-data leaves (Person::Address/Names/… expansions) ARE included now,
    // so demographics carried onto a duplicate are re-confirmed too — only
    // readonly/disabled/derived leaves are skipped.
    if (config?.readonly === true || config?.disabled === true || config?.virtual === true) return;
    if (config?.formula !== undefined || config?.case !== undefined || config?.from_icode !== undefined) return; // derived, not an answer

    // Core leaves mirror the server's live-person read via the snapshot (see doc
    // comment); everything else is judged on the copied answer.
    const value = config?.core_data === true && coreDataSnapshot && name in coreDataSnapshot
      ? coreDataSnapshot[name]
      : superForm[name];
    if (!isPresent(value)) return;

    out[name] = entry;
  });

  return out;
}
